import type { CompanionAgentItem } from "../api/types";
import {
  companionScoreClass,
  companionScoreLabel,
  companionTooltipBullets,
  normalizeCompanionScore,
} from "../lib/companionScore";

// Semicircular gauge geometry (in viewBox units).
const CX = 100;
const CY = 92;
const R = 78;
const NEEDLE_LENGTH = 70;
const ARC_PATH = `M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`;
const ARC_LENGTH = Math.PI * R;

interface CompanionCardProps {
  item: CompanionAgentItem;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

export function CompanionCard({ item, isOpen, onToggle, onClose }: CompanionCardProps) {
  const score = normalizeCompanionScore(item.score);
  const scoreLabel = item.score_label || companionScoreLabel(score);
  const scoreText = score === null ? "--" : String(score);
  const scoreClass = companionScoreClass(score);
  const status = item.status || "ok";
  const tooltipId = `companionTooltip-${item.key || "agent"}`;
  const gradientId = `companionGauge-${item.key || "agent"}`;

  // Value arc fills from the "sell" end; needle points up when unavailable.
  const valueFraction = score === null ? 0 : score / 100;
  const needleFraction = score === null ? 0.5 : score / 100;
  const needleAngle = (needleFraction - 0.5) * 180;

  return (
    <article
      className={`companion-card companion-${status} companion-score-${scoreClass}${
        isOpen ? " is-tooltip-open" : ""
      }`}
      tabIndex={0}
      aria-describedby={tooltipId}
      aria-label={`${item.title || "Agent"}: ${scoreText} out of 100, ${scoreLabel}.`}
      onClick={onToggle}
      onBlur={onClose}
    >
      <div className="companion-card-header">
        <h3>{item.title || "Agent"}</h3>
        <span className={`companion-badge companion-badge-${status}`}>
          {status === "error" ? "Needs attention" : scoreLabel}
        </span>
      </div>
      <div className="companion-gauge" aria-hidden="true">
        <svg className="companion-gauge-svg" viewBox="0 0 200 104" role="img">
          <defs>
            <linearGradient
              id={gradientId}
              gradientUnits="userSpaceOnUse"
              x1={CX - R}
              y1={CY}
              x2={CX + R}
              y2={CY}
            >
              <stop offset="0%" stopColor="var(--red)" />
              <stop offset="50%" stopColor="var(--amber)" />
              <stop offset="100%" stopColor="var(--green)" />
            </linearGradient>
          </defs>
          <path className="companion-gauge-track" d={ARC_PATH} />
          <path
            className="companion-gauge-value"
            d={ARC_PATH}
            stroke={`url(#${gradientId})`}
            strokeDasharray={ARC_LENGTH}
            strokeDashoffset={ARC_LENGTH * (1 - valueFraction)}
          />
          <g
            className="companion-gauge-needle-group"
            style={{ transform: `rotate(${needleAngle}deg)` }}
          >
            <line
              className="companion-gauge-needle"
              x1={CX}
              y1={CY}
              x2={CX}
              y2={CY - NEEDLE_LENGTH}
            />
          </g>
          <circle className="companion-gauge-hub" cx={CX} cy={CY} r={5} />
        </svg>
        <div className="companion-score-value">
          {scoreText}
          <span>/100</span>
        </div>
      </div>
      <div className="companion-tooltip" id={tooltipId} role="tooltip">
        <p className="companion-tooltip-title">{item.error ? "Needs attention" : "Summary"}</p>
        <ul>
          {companionTooltipBullets(item).map((bullet, index) => (
            <li key={index}>{bullet}</li>
          ))}
        </ul>
      </div>
    </article>
  );
}
